import { useMemo, useState } from "react";
import type { MiniAppSessionResponse } from "../../api/types.js";
import type { CheckoutSubmitRequest } from "../../api/types.js";
import { aggregateCartItems } from "../../lib/cart-aggregate.js";
import { useMessages } from "../../i18n/LocaleProvider.js";
import type { CartState } from "../../hooks/useCart.js";
import type { CheckoutState } from "../../hooks/useCheckout.js";
import { ErrorState } from "../ErrorState.js";
import { LoadingState } from "../LoadingState.js";
import { AppButton } from "../ui/AppButton.js";
import { CartLineItem } from "../cart/CartLineItem.js";
import { CartTotals } from "../cart/CartTotals.js";
import { computeTg50Discount } from "../cart/PromoCodePanel.js";
import { CheckoutForm, type CheckoutFormValues } from "./CheckoutForm.js";
import { CheckoutSuccessState } from "./CheckoutSuccessState.js";

const PROMO_STORAGE_KEY = "asiastyle.miniapp.promo_code";

function readStoredPromo(): string | null {
  try {
    const raw = sessionStorage.getItem(PROMO_STORAGE_KEY);
    return raw?.trim() ? raw.trim().toUpperCase() : null;
  } catch {
    return null;
  }
}

interface CheckoutViewProps {
  session: MiniAppSessionResponse;
  cartState: CartState;
  checkoutState: CheckoutState;
  onBackToCart: () => void;
  onBrowseCatalog: () => void;
}

function buildInitialFormValues(session: MiniAppSessionResponse): CheckoutFormValues {
  const client = session.client;
  const fullName = [client?.first_name, client?.last_name].filter(Boolean).join(" ").trim();
  return {
    full_name: fullName || session.telegram_user.first_name,
    phone: client?.phone ?? "",
    delivery_method: "nova_poshta_warehouse",
    city: "",
    warehouse: "",
    address: "",
    comment: "",
    payment_method: "cod",
  };
}

export function CheckoutView({
  session,
  cartState,
  checkoutState,
  onBackToCart,
  onBrowseCatalog,
}: CheckoutViewProps) {
  const messages = useMessages();
  const { loading, cart } = cartState;
  const { submitting, error, result, submit } = checkoutState;
  const [confirmPriceChanges, setConfirmPriceChanges] = useState(false);
  const [formValues, setFormValues] = useState<CheckoutFormValues>(() =>
    buildInitialFormValues(session),
  );
  const appliedPromo = readStoredPromo();

  const aggregatedItems = useMemo(
    () => aggregateCartItems(cart?.items ?? []),
    [cart?.items],
  );
  const isEmpty = aggregatedItems.length === 0;
  const requiresPriceConfirmation = Boolean(cart?.requires_price_confirmation);

  const displayTotals = useMemo(() => {
    const subtotal = cart?.subtotal ?? 0;
    const baseDiscount = cart?.discount_total ?? 0;
    const promo = computeTg50Discount(subtotal, appliedPromo);
    const discountTotal = appliedPromo ? Math.max(baseDiscount, promo) : baseDiscount;
    return {
      subtotal,
      discountTotal,
      total: Math.max(0, subtotal - discountTotal),
      currency: cart?.currency ?? "UAH",
    };
  }, [cart?.subtotal, cart?.discount_total, cart?.currency, appliedPromo]);

  if (result) {
    return <CheckoutSuccessState result={result} onBrowseCatalog={onBrowseCatalog} />;
  }

  if (loading && !cart) {
    return <LoadingState label={messages.checkout.loading} />;
  }

  if (isEmpty) {
    return (
      <section className="flex flex-1 flex-col gap-4 p-4">
        <p className="text-sm opacity-70">{messages.checkout.emptyCart}</p>
        <AppButton onClick={onBrowseCatalog}>{messages.checkout.browseCatalog}</AppButton>
      </section>
    );
  }

  if (!cart) {
    return <ErrorState message={messages.checkout.unavailable} onRetry={cartState.reload} />;
  }

  async function handleSubmit() {
    const promoNote =
      appliedPromo === "TG50"
        ? ` [promo:${appliedPromo} disc=${computeTg50Discount(cart?.subtotal ?? 0, appliedPromo)}]`
        : "";
    const payload: CheckoutSubmitRequest = {
      full_name: formValues.full_name,
      phone: formValues.phone,
      delivery_method: formValues.delivery_method,
      city: formValues.city || null,
      warehouse: formValues.warehouse || null,
      address: formValues.address || null,
      comment: `${formValues.comment || ""}${promoNote}`.trim() || null,
      payment_method: formValues.payment_method,
    };
    if (requiresPriceConfirmation) {
      payload.confirm_price_changes = confirmPriceChanges;
    }
    await submit(payload);
  }

  return (
    <section className="flex flex-1 flex-col gap-4 px-4 py-4 pb-28">
      <button
        type="button"
        onClick={onBackToCart}
        className="self-start min-h-11 text-sm font-medium text-[var(--tg-theme-link-color,#3390ec)]"
      >
        {messages.checkout.backToCart}
      </button>

      <h2 className="text-lg font-semibold">{messages.checkout.title}</h2>

      <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
        {messages.checkout.stagingBetaBanner ? (
          <p className="font-medium">{messages.checkout.stagingBetaBanner}</p>
        ) : null}
        <p className="mt-1 opacity-90">{messages.checkout.paymentNote}</p>
        <p className="mt-1 opacity-90">{messages.checkout.npNote}</p>
        <p className="mt-1 opacity-90">{messages.checkout.tg50Note}</p>
        {appliedPromo ? (
          <p className="mt-1 font-medium opacity-90">
            {computeTg50Discount(displayTotals.subtotal, appliedPromo) > 0
              ? messages.cart.promoTg50Applied(
                  computeTg50Discount(displayTotals.subtotal, appliedPromo),
                )
              : messages.cart.promoTg50UnderMin(500)}
          </p>
        ) : null}
      </div>

      {requiresPriceConfirmation ? (
        <div className="rounded-[16px] border border-orange-200 bg-orange-50 p-4 dark:border-orange-900/40 dark:bg-orange-950/20">
          <h3 className="text-sm font-semibold">{messages.checkout.priceChangeTitle}</h3>
          <p className="mt-1 text-sm opacity-80">{messages.checkout.priceChangeBody}</p>
          <label className="mt-3 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmPriceChanges}
              onChange={(event) => setConfirmPriceChanges(event.target.checked)}
              className="mt-1"
            />
            <span>{messages.checkout.confirmPriceChanges}</span>
          </label>
        </div>
      ) : null}

      <div className="rounded-[16px] border border-black/5 p-4">
        <h3 className="mb-2 text-sm font-semibold opacity-80">{messages.checkout.summary}</h3>
        <ul className="mb-3 flex flex-col gap-2" role="list">
          {aggregatedItems.map((item) => (
            <li key={item.product_id}>
              <CartLineItem item={item} disabled readOnly />
            </li>
          ))}
        </ul>
        <CartTotals
          subtotal={displayTotals.subtotal}
          discountTotal={displayTotals.discountTotal}
          total={displayTotals.total}
          currency={displayTotals.currency}
        />
      </div>

      <CheckoutForm values={formValues} onChange={setFormValues} disabled={submitting} />

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <AppButton
        disabled={submitting || (requiresPriceConfirmation && !confirmPriceChanges)}
        onClick={() => void handleSubmit()}
      >
        {submitting
          ? messages.checkout.submitting
          : requiresPriceConfirmation && !confirmPriceChanges
            ? messages.checkout.confirmPriceChanges
            : messages.checkout.submit}
      </AppButton>
    </section>
  );
}
